import moment from 'moment';

/**
 * Checks if the current value is a valid date. If so, appends `UTC` to
 * the end of the date string.
 * @param {string} value The cell value.
 * @returns A date string or the original value.
 */
const processPossibleDateCellValue = value => {
  // TODO: Possibly make a `valueGetter` callback function that allows the
  // caller to accomplish this if requirements change later.
  const date = moment.utc(value, 'YYYY-MM-DD HH:mm:ss', true);
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss z') : value;
};

const serializeCellValue = (value, delimiterCharacter = ',') => {
  // Cell value must be a string otherwise there's a chance it won't be formatted
  // properly.
  if (typeof value !== 'string') {
    return value;
  }

  const processedValue = processPossibleDateCellValue(value);
  const formattedValue = processedValue.replace(/"/g, '""');

  const shouldEncloseValue = [delimiterCharacter, '\n', '\r'].some(char =>
    formattedValue.includes(char)
  );

  return shouldEncloseValue ? `"${formattedValue.trim()}"` : formattedValue.trim();
};

const buildColumns = columns =>
  columns
    .map(({ label }) => label)
    .join(',')
    .trim();

const buildRows = (rows, columns) => {
  const rowsToExport = [];

  for (let i = 0; i < rows.length; i++) {
    const row = [];

    for (let j = 0; j < columns.length; j++) {
      const { id } = columns[j];
      const value = serializeCellValue(rows[i][id] ?? '');
      row.push(value);
    }

    rowsToExport.push(row.join(',').trim());
  }

  return rowsToExport;
};

/**
 * Builds a string that can be exported to CSV format via a `Blob`.
 * All values not included in `columns` will be omitted from the final string.
 *
 * @example
 * const rows = [{ projectName: 'test name', referenceNumber: 'MX02' }];
 * const columns = [{ id: 'projectName', label: 'Project' }, { id: 'referenceNumber', label: 'Ref. #' }];
 *
 * buildCsv(rows, columns) ->
 * `Project,Ref. #
 * test name,MX02`
 *
 * @param {Array<object>} rows The table rows to export.
 * @param {Array<object>} columns The table columns to export.
 * @returns A string where each new line is separated by commas.
 */
export const buildCsv = (rows, columns) => {
  const csv = [];

  const columnsToExport = buildColumns(columns);
  const rowsToExport = buildRows(rows, columns);
  csv.push(columnsToExport, ...rowsToExport);

  return csv.join('\n').trim();
};

/**
 * Attempts to export a comma separated string into a CSV format.
 * This is only supported for modern browsers.
 *
 * @param {string} csv The comma separated string to export.
 * @param {object} options An object containing the file name options.
 */
export const exportCsv = (csv, options) => {
  const { fileName = 'untitled', appendDate = false } = options;

  const now = moment().format('YYYY-MM-DD');
  const name = appendDate ? `${fileName} - ${now}` : `${fileName}`;
  const fullName = `${name}.csv`;

  const blob = new Blob([csv], { type: 'text/csv' });

  if ('download' in HTMLAnchorElement.prototype) {
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fullName;

    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
    });
  }
};
